import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { collectTracking } from "@/lib/utm";
import LeadFields, { Field } from "@/components/LeadFields";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PORTFOLIO_SITE_COUNTS, PORTFOLIO_TYPES, PORTFOLIO_TIMELINES } from "@/lib/constants";
import { ArrowRight, CheckCircle2, Layers } from "lucide-react";

const selectCls = "bg-[#12141A] border-white/10 rounded-none h-11";

export default function PortfolioScreening() {
  const [values, setValues] = useState({
    first_name: "", last_name: "", email: "", mobile: "", company: "", buyer_type: "",
    number_of_sites: "", geographic_area: "", development_type: "", portfolio_timeline: "",
    message: "", consent: false,
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) return toast.error("Please tick the consent box to continue.");
    for (const k of ["number_of_sites", "geographic_area", "development_type"]) {
      if (!values[k]) return toast.error(`Please complete: ${k.replace(/_/g, " ")}`);
    }
    setSubmitting(true);
    try {
      await api.post("/leads/portfolio-screening", {
        ...values,
        tracking: collectTracking({ formType: "portfolio_screening", enquiryType: "pfas_src_portfolio_screening" }),
      });
      setDone(true);
      toast.success("Portfolio Screening enquiry received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <SuccessBlock />;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <div className="kicker mb-4 flex items-center gap-3"><Layers className="w-3.5 h-3.5 text-emerald-400" /> Portfolio Screening · Market validation · Pricing on discussion</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight" data-testid="portfolio-title">Reviewing multiple sites?</h1>
      <p className="mt-6 text-white/65 max-w-2xl leading-relaxed">
        For developers, landowners and advisers evaluating several opportunities, PFAS Risk Intelligence can provide portfolio-level source screening to help identify which sites warrant deeper review first.
      </p>
      <p className="mt-3 text-white max-w-2xl">Prioritise the sites that need attention before committing full investigation spend.</p>

      <form onSubmit={submit} className="mt-10 border border-white/10 bg-[#0C0E13] p-6 md:p-10 space-y-10">
        <section>
          <div className="kicker mb-6">Your details</div>
          <LeadFields values={values} onChange={setField} idPrefix="portfolio"
            consentLabel="I agree to be contacted about my Portfolio Screening enquiry and receive relevant PFAS land-risk information." />
        </section>

        <section className="border-t border-white/[0.06] pt-8 space-y-5">
          <div className="kicker mb-2">The portfolio</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Approximate number of sites" required>
              <Select value={values.number_of_sites} onValueChange={(v) => setField("number_of_sites", v)}>
                <SelectTrigger data-testid="portfolio-number-of-sites" className={selectCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white">
                  {PORTFOLIO_SITE_COUNTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Geographic area" required>
              <Input value={values.geographic_area} onChange={(e) => setField("geographic_area", e.target.value)}
                data-testid="portfolio-geographic-area" className={selectCls} required
                placeholder="e.g. South West London, M62 corridor, national" />
            </Field>
            <Field label="Development / portfolio type" required>
              <Select value={values.development_type} onValueChange={(v) => setField("development_type", v)}>
                <SelectTrigger data-testid="portfolio-development-type" className={selectCls}><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white">
                  {PORTFOLIO_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Timeline">
              <Select value={values.portfolio_timeline} onValueChange={(v) => setField("portfolio_timeline", v)}>
                <SelectTrigger data-testid="portfolio-timeline" className={selectCls}><SelectValue placeholder="Select timeline" /></SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white">
                  {PORTFOLIO_TIMELINES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Message" className="md:col-span-2">
              <Textarea rows={5} value={values.message} onChange={(e) => setField("message", e.target.value)}
                data-testid="portfolio-message" placeholder="What decision are you trying to make, and by when? Any known PFAS-relevant history across the portfolio?"
                className="bg-[#12141A] border-white/10 rounded-none" />
            </Field>
          </div>
        </section>

        <div className="pt-2 flex flex-wrap gap-4 items-center">
          <button type="submit" disabled={submitting} data-testid="portfolio-submit-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90 disabled:opacity-40">
            {submitting ? "Sending…" : "Discuss Portfolio Screening"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
          <span className="text-xs text-white/40 max-w-md">
            No fixed price is published for portfolio work. We will discuss scope, evidence available and prioritisation approach before any proposal.
          </span>
        </div>
      </form>
    </div>
  );
}

function SuccessBlock() {
  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-24" data-testid="portfolio-success">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={2} />
        </div>
        <div className="kicker text-emerald-400">Received</div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Thank you. We have received your Portfolio Screening enquiry.</h1>
      <p className="mt-8 text-white/70 leading-relaxed max-w-3xl">
        We will review the portfolio context you have shared and contact you to discuss the number of sites, the evidence already available and how a prioritised PFAS source screen could support your decision.
      </p>
      <div className="mt-10 border-t border-white/[0.08] pt-6 text-xs text-white/40">
        Your enquiry has been logged securely. Response time: normally within one working day.
      </div>
    </div>
  );
}
